import React, { createContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getCargoRealNamesPaginated } from 'actions/cargoes/realNames';
import { getCargoTagsCategories } from 'actions/cargoes/tags';
import AddTagsModal from './AddTagsModal';
import { Checkbox, CircularProgress, FlatButton, RaisedButton, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TextField } from 'material-ui';
import Pagination from 'components/Libs/Pagination';
import RealNamesRow from './RealNamesRow';
import styles from 'styles';
import { getCargoAssignersPost } from 'actions/cargoes/assigners';
import ConvertCargoDialog from './ConvertCargoDialog';
import api from 'services';
import { showNotification } from '../../../helpers';

const rowsPerPage = [50, 100];

function FightRerenderWrapper({ children }) {
    return (
        <div className='table-body'>
            { children }
        </div>
    );
}

/**
 * 
 * @param {{
 *  rows: any[],
 *  cargoTypes: any[],
 *  SEARATES_CATEGORIES: any[],
 * }} param0
 * @returns 
 */
function _table({
    rows, cargoTypes, SEARATES_CATEGORIES, 
}) {
    return (
        <Table selectable={false}>
            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                <TableRow>
                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnCSS, width: '20%', maxWidth: 'unset' }}>
                        Real Cargo name
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnCSS, width: '20%', maxWidth: 'unset' }}>
                        Cargo type
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '10%', maxWidth: 'unset' }}>
                        Details
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '7%', maxWidth: 'unset' }}>
                        SF
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '20%', maxWidth: 'unset' }}>
                        Tags
                    </TableHeaderColumn>
                    <TableHeaderColumn style={{ ...styles.tableHeaderColumnSmallCSS, width: '23%', maxWidth: 'unset' }}>
                        Management
                    </TableHeaderColumn>
                </TableRow>
            </TableHeader>

            <TableBody displayRowCheckbox={false}>
                {
                    rows.map((r, index) => (
                        <RealNamesRow 
                            key={`r_${index}`}
                            item={r} 
                            cargoTypes={cargoTypes} 
                            SEARATES_CATEGORIES={SEARATES_CATEGORIES}
                        />
                    ))
                }
            </TableBody>
        </Table>
    );
}

const NamesTable = React.memo(_table);

export const IsCargoesFromUsersContext = createContext(null);
export const HandleToggleConvertDialogContext = createContext(null);

/**
 * 
 * @param {RealNamesTableProps} props
 */
function RealNamesTable(props) {
    const [search, setSearch] = useState(undefined);
    const [searchApplied, setSearchApplied] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [rows, setRows] = useState(props.items);
    const [total, setTotal] = useState(0);
    const [manageTagsModalOpen, setManageTagsModalOpen] = useState(false);
    const [initialTagsCategories, setInitialTagsCategories] = useState([]);
    const [isCargoesFromUsers, setIsCargoesFromUsers] = useState(false);
    const [openConvertDialog, setOpenConvertDialog] = useState(false);
    const [currentCargoId, setCurrentCargoId] = useState(null);

    const changeSearch = useCallback(
        (value) => {
            setSearch(value);
        },
        [],
    );
    const getCategories = () => {
        props.getCargoTagsCategories();
    };

    useEffect(() => {
        props.getCargoRealNamesPaginated({ limit, page, filters: { search } });
        getCategories();
    }, []);

    useEffect(() => {
        props.items.length && props.getCargoAssignersPost(props.items.map(i => i._id));
    }, [props.items]);

    const searchNames = ({
        newPage = 1, newLimit = 50, newSearchApplied = false, additionalFilters = { isCargoesFromUsers },
    }) => {
        props.getCargoRealNamesPaginated({ 
            page: newPage, 
            limit: newLimit,
            filters: { 
                search: newSearchApplied ? search : undefined,
                ...additionalFilters,
            },
        });
    };

    useEffect(() => {
        setInitialTagsCategories(props.cargoTagsCategories);
    }, [props.cargoTagsCategories]);

    useEffect(() => {
        setTotal(props.total);
        setRows(props.items);
    }, [props.total, props.items, props.page, props.limit]);

    const handleToggleManageTags = () => {
        setManageTagsModalOpen(!manageTagsModalOpen);
    };

    const handleUpdateRow = ({ page: _page, numberOfRows }) => {
        setLimit(numberOfRows);
        setPage(_page);
        searchNames({ 
            newPage: _page,
            newLimit: numberOfRows,
            newSearchApplied: !!searchApplied,
        });
    };

    const handleClickSearch = () => {
        setPage(1);
        setSearchApplied(!!search);
        searchNames({ 
            newPage: 1, 
            newLimit: limit, 
            newSearchApplied: !!search,
        });
    };

    const handleCheckFromUsers = () => {
        setPage(1);
        setSearchApplied(!!search);
        setIsCargoesFromUsers(!isCargoesFromUsers);
        searchNames({
            newPage: 1, 
            newLimit: limit, 
            newSearchApplied: true, 
            additionalFilters: { isCargoesFromUsers: !isCargoesFromUsers }, 
        });
    };

    const handleToggleConvertDialog = (cargoId) => {
        if (openConvertDialog) {
            setCurrentCargoId(null);
        } else {
            setCurrentCargoId(cargoId);
        }

        setOpenConvertDialog(!openConvertDialog);
    };

    const handleConvert = () => {
        api.convertUserCargoToAdmin(currentCargoId)
            .then((response) => {
                if (response.status === 200) {
                    showNotification(
                        `Cargo converted successfully. ${response.data.data.hasDuplicates ? 'Cargo has duplicates.' : ''}`,
                        'success',
                    );
                    setOpenConvertDialog(!openConvertDialog);
                    props.getCargoRealNamesPaginated({ limit, page, filters: { search } });
                    setCurrentCargoId(null);
                } else {
                    showNotification(
                        `Failed to convert cargo. Error: ${response.errors[0].messages[0]}`,
                        'error',
                    );
                }
            });
    };

    const actionsConvert = [
        <FlatButton
            label='Close'
            primary
            onClick={handleToggleConvertDialog}
        />,
        <FlatButton
            label='Convert'
            primary
            onClick={handleConvert}
        />,
    ];

    return (
        <IsCargoesFromUsersContext.Provider value={isCargoesFromUsers}>
            <HandleToggleConvertDialogContext.Provider value={handleToggleConvertDialog}>
                <div>
                    {
                        openConvertDialog ?
                            <ConvertCargoDialog 
                                open={openConvertDialog}
                                actions={actionsConvert}
                                handleClose={handleToggleConvertDialog}
                                handleConvert={handleConvert}
                            /> : null
                    }
                    <AddTagsModal 
                        manageTagsModalOpen={manageTagsModalOpen}
                        handleToggleManageTags={handleToggleManageTags}
                        getCategories={getCategories}
                        initialTagsCategories={initialTagsCategories}
                    />
                    <div className='table-header'>
                        <div className='item management'>
                            <TextField
                                value={search || ''}
                                floatingLabelText='Search'
                                onChange={e => changeSearch(e.target.value || undefined)}
                                onKeyDown={e => e.keyCode === 13 && handleClickSearch()}
                            />
                            <Checkbox 
                                label='User cargoes'
                                checked={isCargoesFromUsers}
                                onCheck={handleCheckFromUsers}
                                style={{ width: 'unset' }}
                                labelStyle={{ fontSize: '13px', width: '100%' }}
                            />
                            <RaisedButton
                                label='Search'
                                onClick={handleClickSearch}
                                primary
                            />
                            <RaisedButton 
                                onClick={handleToggleManageTags}
                                label='Manage Tags'
                                primary
                            />
                        </div>

                        <div className='item'>
                            <Pagination
                                total={total}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                numberOfRows={limit}
                                updateRows={handleUpdateRow}
                            />
                        </div>
                    </div>
                    {
                        props.isLoaded ?
                            <FightRerenderWrapper>
                                <NamesTable
                                    rows={rows}
                                    cargoTypes={props.cargoTypes} 
                                    SEARATES_CATEGORIES={props.seaRatesCategories}  
                                />
                            </FightRerenderWrapper> :
                            <CircularProgress
                                style={styles.circularProgress}
                            />
                    }
                </div>
            </HandleToggleConvertDialogContext.Provider>
        </IsCargoesFromUsersContext.Provider>
    );
}

RealNamesTable.defaultProps = {
    total: 0,
    page: 1,
    limit: 50,
    items: [],
    isLoaded: false,
    getCargoRealNamesPaginated: () => {},
    getCargoTagsCategories: () => {},
    getCargoAssignersPost: () => {},
    cargoTypes: [],
    seaRatesCategories: [],
};

RealNamesTable.propTypes = {
    total: PropTypes.number,
    page: PropTypes.number,
    limit: PropTypes.number,
    items: PropTypes.array,
    isLoaded: PropTypes.bool,
    getCargoRealNamesPaginated: PropTypes.func,
    getCargoAssignersPost: PropTypes.func,
    getCargoTagsCategories: PropTypes.func,
    cargoTypes: PropTypes.array,
    seaRatesCategories: PropTypes.array,
};

export default connect(
    (state) => {
        const {
            items, total, filters, page, limit, isLoaded,
            categories: seaRatesCategories,
        } = state.cargoRealNames;

        const { items: cargoTagsCategories } = state.cargoTagsCategories;
        const { cargoTypes } = state;

        if (items.length && state.cargoAssigners.items.length) {
            items.forEach((row) => {
                state.cargoAssigners.items.forEach((cargo) => {
                    if (row._id === cargo.realCargoId && cargo.seaRatesCategories && cargo.seaRatesCategories.length) {
                        row.hasSeaRates = true;
                    }
                });
            });
        }

        return {
            items, 
            total,
            search: filters ? filters.search : undefined, 
            page, 
            limit,
            isLoaded,
            cargoTagsCategories,
            cargoTypes,
            seaRatesCategories,
        };
    },
    dispatch => bindActionCreators(
        {
            getCargoTagsCategories,
            getCargoRealNamesPaginated,
            getCargoAssignersPost,
        },
        dispatch,
    ),
)(RealNamesTable);

/**
 * props
 * @typedef {Object} RealNamesTableProps
 * @property {any[]} items 
 * @property {number} total
 * @property {string} search
 * @property {number} page
 * @property {number} limit
 * @property {boolean} isLoaded
 * @property {Function} getCargoRealNamesPaginated
 * @property {Function} getCargoTagsCategories
 * @property {any[]} cargoTagsCategories
 * @property {any[]} cargoTypes
 * @property {any[]} seaRatesCategories
 * @property {Function} getCargoAssignersPost
 */



// WEBPACK FOOTER //
// ./app/components/Cargoes/RealNames/RealNamesTable.jsx